import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AxiosInstance } from 'axios';
import { Icons } from '@density/dust';
import { CoreSpace } from '@densityco/lib-api-types';
import * as dust from '@density/dust/dist/tokens/dust.tokens';
import { toast } from 'react-toastify';

import { Action } from './actions';
import { PasteChoice, State } from './state';
import styles from './styles.module.scss';

import Button from 'components/button';
import SelectField from 'components/select-field';
import { DateAndTimeTextField } from 'components/date-text-field';
import TextField from 'components/text-field';
import Tooltip from 'components/tooltip';
import Popup from 'components/popup';
import { LengthUnit } from 'lib/units';
import { CoreAPI, FloorplanAPI, FloorplanV2Plan } from 'lib/api';
import { Analytics } from 'lib/analytics';
import { z } from 'zod';

import { FixMe } from 'types/fixme';
import KeybindingsModal from './keybindings/keybindings-modal';
import Modal from 'components/modal';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SPLITS } from 'lib/treatments';
import { useTreatment } from 'contexts/treatments';
import StaffDriftButton from './staff-drift-button';

type PastingMode =
  | PasteChoice.ROTATION
  | PasteChoice.HEIGHT
  | PasteChoice.INCREMENT
  | PasteChoice.CAPACITY
  | PasteChoice.FUNCTION
  | PasteChoice.LABELS
  | PasteChoice.TYPE
  | PasteChoice.SHAPE;

type UnitChoice = { id: LengthUnit; label: string };
type MetadataChoice = {
  id: string;
  label: string;
  editable: boolean;
  type: 'number' | 'string' | 'cost' | 'currency' | 'list' | 'date' | 'time';
  choices?: { id: string | null; label: string }[];
};
type PastingModeOption = { id: PastingMode; label: PastingMode };

type PastingChoice = {
  space: PastingModeOption[];
  sensor: PastingModeOption[];
};

interface FormFields {
  name: string;
  floor_level: number | null;
  description: string | null;
  capacity: number | null;
  target_capacity: number | null;
  safe_capacity: number | null;
  safe_threshold_mode: string | null;
  cost_per_sqft: number | null;
  cost_per_sqft_currency: string | null;
  iwms_id: string | null;
  counting_mode: string | null;
  daily_reset: string | null;
}

type CoreSpace2024 = CoreSpace & {
  go_live_date_utc: string | null;
  cost_per_sqft_currency: string | null;
  cost_per_sqft: number | null;
  iwms_id: string | null;
};

const UNIT_CHOICES: Array<UnitChoice> = [
  { id: 'feet_and_inches', label: 'ft & in' },
  { id: 'inches', label: 'in' },
  { id: 'meters', label: 'm' },
  { id: 'centimeters', label: 'cm' },
  { id: 'millimeters', label: 'mm' },
];

const PASTING_CHOICES: PastingChoice = {
  space: [
    { id: PasteChoice.CAPACITY, label: PasteChoice.CAPACITY },
    { id: PasteChoice.LABELS, label: PasteChoice.LABELS },
    { id: PasteChoice.FUNCTION, label: PasteChoice.FUNCTION },
    { id: PasteChoice.INCREMENT, label: PasteChoice.INCREMENT },
    { id: PasteChoice.SHAPE, label: PasteChoice.SHAPE },
  ],
  sensor: [
    { id: PasteChoice.ROTATION, label: PasteChoice.ROTATION },
    { id: PasteChoice.HEIGHT, label: PasteChoice.HEIGHT },
    { id: PasteChoice.TYPE, label: PasteChoice.TYPE },
  ],
};

// The data we want to include in the text fields in the Modal
// from the space data
const METADATA_FIELDS: Array<MetadataChoice> = [
  { id: 'name', label: 'Name', editable: true, type: 'string' },
  { id: 'floor_level', label: 'Floor Level', editable: true, type: 'number' },
  { id: 'description', label: 'Description', editable: true, type: 'string' },
  { id: 'capacity', label: 'Capacity', editable: true, type: 'number' },
  {
    id: 'target_capacity',
    label: 'Target capacity',
    editable: true,
    type: 'number',
  },
  {
    id: 'safe_capacity',
    label: 'Safe capacity',
    editable: true,
    type: 'number',
  },
  {
    id: 'safe_threshold_mode',
    label: 'Safe threshold mode',
    editable: true,
    type: 'list',
    choices: [
      { id: null, label: 'None' },
      { id: 'SAFE_CAPACITY', label: 'Safe Capacity' },
      { id: 'UTILIZATION_PERC_LEGAL', label: 'Utilization % Legal' },
      { id: 'UTILIZATION_PERC_TARGET', label: 'Utilization % Target' },
    ],
  },
  {
    id: 'cost_per_sqft',
    label: 'Cost per sqft',
    editable: true,
    type: 'cost',
  },
  {
    id: 'cost_per_sqft_currency',
    label: 'Cost per sqft currency',
    editable: false,
    type: 'currency',
  },
  { id: 'iwms_id', label: 'IWMS ID', editable: true, type: 'string' },
  {
    id: 'counting_mode',
    label: 'Counting mode',
    editable: true,
    type: 'list',
    choices: [
      { id: null, label: 'None' },
      { id: 'oa', label: 'Open Area' },
      { id: 'entry', label: 'Entry' },
    ],
  },
  { id: 'daily_reset', label: 'Daily reset', editable: true, type: 'time' },

  { id: 'created_at', label: 'Created at', editable: false, type: 'date' },
  { id: 'updated_at', label: 'Updated at', editable: false, type: 'date' },
];

const formSchema = z
  .object({
    name: z.string().min(1, { message: 'Floor must have a name' }),
    floor_level: z.number().or(z.null()),
    description: z.string().or(z.null()),
    capacity: z
      .number()
      .int()
      .min(1, { message: 'Must be a positive number empty' })
      .or(z.null()),
    target_capacity: z
      .number()
      .int()
      .min(1, { message: 'Must be a positive number or empty' })
      .or(z.null()),
    safe_capacity: z
      .number()
      .int()
      .min(1, { message: 'Must be a positive number or empty' })
      .or(z.null()),
    safe_threshold_mode: z.string().or(z.null()),
    cost_per_sqft: z
      .number()
      .min(0, { message: 'Must be a positive number or empty' })
      .or(z.null()),
    iwms_id: z.string().or(z.null()),
    counting_mode: z.string().or(z.null()),
    daily_reset: z.string().or(z.null()),
  })
  .superRefine((data, ctx) => {
    if (data.safe_threshold_mode === 'SAFE_CAPACITY' && !data.safe_capacity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Safe capacity is required when mode is Safe Capacity',
        path: ['safe_capacity'],
      });
    }
    if (
      data.safe_threshold_mode === 'UTILIZATION_PERC_LEGAL' &&
      !data.capacity
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Capacity is required when mode is Utilization % Legal',
        path: ['capacity'],
      });
    }
    if (
      data.safe_threshold_mode === 'UTILIZATION_PERC_TARGET' &&
      !data.target_capacity
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Target capacity is required when mode is Utilization % Target',
        path: ['target_capacity'],
      });
    }
  });

const SettingsPanelForm = ({
  updateSpace,
  visible,
  toggleModal,
  fieldValues,
}: {
  updateSpace: (data: FormFields) => void;
  visible: boolean;
  toggleModal: () => void;
  fieldValues: FormFields;
}) => {
  // react-hook-form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      ...fieldValues,
    },
  });

  const countingMode = useWatch({ control, name: 'counting_mode' });

  const handleMetadataSubmission = (e: FormFields) => {
    try {
      updateSpace(e);
    } catch (error) {
      console.error(error);
    }
    toggleModal();
  };

  return (
    <Modal width={600} visible={visible} onBlur={toggleModal}>
      <form onSubmit={handleSubmit(handleMetadataSubmission)}>
        <div className={styles.spaceMetaHeader}>Floor Metadata</div>
        <div className={styles.spaceMetaContent}>
          {METADATA_FIELDS.map((field) => {
            const { type, label, choices, editable } = field;
            const fieldType =
              type === 'cost' ? 'number' : type === 'date' ? 'string' : type;
            const id = field.id as keyof FormFields;
            const currentVals = getValues();
            const isDailyWithOa = countingMode === 'oa' && id === 'daily_reset';
            if (type === 'currency') return null; // Skip currency fields
            return (
              <div className={styles.spaceMetaRow} key={id}>
                <div className={styles.spaceMetaRowLabel}>
                  {label}
                  {id === 'cost_per_sqft' &&
                    ` (${currentVals['cost_per_sqft_currency']})`}
                </div>
                <Controller
                  name={id as keyof FormFields}
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    if (type === 'list') {
                      return (
                        <SelectField
                          size="medium"
                          data-cy={`settings-metadata-${id}`}
                          value={value || ''}
                          onChange={(choice) => {
                            setValue(id, choice.id);
                            if (choice.id === 'oa') {
                              setValue('daily_reset', null);
                            }
                            if (id === 'safe_threshold_mode') {
                              trigger([
                                'safe_capacity',
                                'capacity',
                                'target_capacity',
                              ]);
                            }
                          }}
                          choices={choices || []}
                        />
                      );
                    } else {
                      return (
                        <div className={styles.spaceMetaRowInput}>
                          <TextField
                            size="medium"
                            type={fieldType}
                            data-cy={`settings-metadata-${id}`}
                            disabled={!editable || isDailyWithOa}
                            placeholder="null"
                            value={
                              type === 'date'
                                ? new Date(value as string).toLocaleString()
                                : value ?? ''
                            }
                            error={errors[id]?.message ? true : false}
                            onChange={(e) => {
                              let v: string | number | null =
                                e.currentTarget.value;
                              if (fieldType === 'number') {
                                v = parseFloat(v);
                                if (isNaN(v)) v = null;
                              }
                              onChange(v);
                            }}
                          />
                          {errors[id]?.message && (
                            <p className={styles.floorMetadataError}>
                              {errors[id]?.message}
                            </p>
                          )}
                        </div>
                      );
                    }
                  }}
                />
              </div>
            );
          })}
          <div className={styles.spaceMetaRow}>
            <Button type="hollow" onClick={toggleModal}>
              Cancel
            </Button>
            <Button type="filled" buttonType="submit">
              Save
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

type FloorDebounceSettingsPayload = {
  includeLocked: boolean;
  presenceDebounceEnabled: boolean;
  presenceDebounceTrueMs: number | null;
  presenceDebounceFalseMs: number | null;
};

type FloorDebounceApplyResult =
  | { success: true; updatedCount: number }
  | { success: false; errorMessage: string };

type FloorDebounceEnabledChoice = 'null' | 'true' | 'false';

type FloorDebounceSpaceLike = {
  presenceDebounceEnabled: boolean | null;
  presenceDebounceTrueMs: number | null;
  presenceDebounceFalseMs: number | null;
};

const FLOOR_DEBOUNCE_DEFAULT_FORM_VALUES = {
  enabledChoice: 'false' as FloorDebounceEnabledChoice,
  ingressMs: '',
  egressMs: '',
};

function toFloorDebounceEnabledChoice(
  value: FloorDebounceSpaceLike['presenceDebounceEnabled']
): FloorDebounceEnabledChoice {
  if (value == null) {
    return 'null';
  }
  return value ? 'true' : 'false';
}

function getUniformFloorDebounceValues(spaces: FloorDebounceSpaceLike[]): {
  enabledChoice: FloorDebounceEnabledChoice;
  ingressMs: string;
  egressMs: string;
} | null {
  if (!spaces.length) {
    return null;
  }

  const [firstSpace, ...remainingSpaces] = spaces;
  const firstEnabled = firstSpace.presenceDebounceEnabled ?? null;
  const firstIngressMs = firstSpace.presenceDebounceTrueMs ?? null;
  const firstEgressMs = firstSpace.presenceDebounceFalseMs ?? null;

  const hasUniformDebounceSettings = remainingSpaces.every((space) => {
    const enabled = space.presenceDebounceEnabled ?? null;
    const ingressMs = space.presenceDebounceTrueMs ?? null;
    const egressMs = space.presenceDebounceFalseMs ?? null;

    return (
      enabled === firstEnabled &&
      ingressMs === firstIngressMs &&
      egressMs === firstEgressMs
    );
  });

  if (!hasUniformDebounceSettings) {
    return null;
  }

  return {
    enabledChoice: toFloorDebounceEnabledChoice(firstEnabled),
    ingressMs: firstIngressMs == null ? '' : `${firstIngressMs}`,
    egressMs: firstEgressMs == null ? '' : `${firstEgressMs}`,
  };
}

const SettingsPanel: React.FunctionComponent<{
  state: State;
  plan: FloorplanV2Plan;
  client: AxiosInstance;
  editStatusLoading: boolean;
  onEditStatus: (status: CoreSpace['status']) => Promise<void>;
  dispatch: React.Dispatch<Action>;
  onApplyDebounceToAllSpaces: (
    settings: FloorDebounceSettingsPayload
  ) => Promise<FloorDebounceApplyResult>;
}> = ({
  state,
  plan,
  client,
  editStatusLoading,
  onEditStatus,
  dispatch,
  onApplyDebounceToAllSpaces,
}) => {
  const [floorReloadCounter, setFloorReloadCounter] = useState(0);
  const [showSpaceMetadataModal, setShowSpaceMetadataModal] = useState(false);
  const isDriftLinksEnabled = useTreatment(SPLITS.GRAFANA_LINKS);

  const reloadFloor = useCallback(
    () => setFloorReloadCounter((n) => n + 1),
    [setFloorReloadCounter]
  );
  const [showKeybindingsModal, setShowKeybindingsModal] = useState(false);
  const [isFloorDebounceExpanded, setIsFloorDebounceExpanded] = useState(false);
  const [floorDebounceEnabledChoice, setFloorDebounceEnabledChoice] =
    useState<FloorDebounceEnabledChoice>(
      FLOOR_DEBOUNCE_DEFAULT_FORM_VALUES.enabledChoice
    );
  const [floorDebounceTrueMs, setFloorDebounceTrueMs] = useState(
    FLOOR_DEBOUNCE_DEFAULT_FORM_VALUES.ingressMs
  );
  const [floorDebounceFalseMs, setFloorDebounceFalseMs] = useState(
    FLOOR_DEBOUNCE_DEFAULT_FORM_VALUES.egressMs
  );
  const [floorDebounceIncludeLocked, setFloorDebounceIncludeLocked] =
    useState(false);
  const [isApplyingFloorDebounce, setIsApplyingFloorDebounce] = useState(false);
  const [showRecenterConfirm, setShowRecenterConfirm] = useState(false);
  const [isRecenteringOrigin, setIsRecenteringOrigin] = useState(false);
  const wasSettingsOpen = useRef(state.panels.settingsOpen);
  const getSpaceData = useCallback(
    (abortController: AbortController) => {
      CoreAPI.getSpace(client, plan.floor.id, abortController.signal)
        .then((response) => {
          setFloor({
            status: 'complete' as const,
            data: response.data as CoreSpace2024,
          });
        })
        .catch((err) => {
          if ((err as FixMe).name === 'CanceledError') {
            return;
          }
          setFloor({ status: 'error' as const });
        });
    },
    [client, plan.floor.id]
  );
  // Load the full data for the currently focused space (of type floor) when opening the settings
  // panel.
  const [floor, setFloor] = useState<
    | { status: 'pending' }
    | { status: 'loading' }
    | {
        status: 'complete';
        data: CoreSpace2024;
      }
    | {
        status: 'complete-and-updating';
        data: CoreSpace2024;
      }
    | { status: 'error' }
  >({ status: 'pending' as const });
  useEffect(() => {
    if (!state.panels.settingsOpen) {
      setFloor({ status: 'pending' as const });
      return;
    }

    const abortController = new AbortController();
    setFloor({ status: 'loading' as const });
    getSpaceData(abortController);

    return () => {
      abortController.abort();
      setFloor({ status: 'pending' as const });
    };
  }, [
    state.panels.settingsOpen,
    client,
    floorReloadCounter,
    plan.floor.id,
    getSpaceData,
  ]);

  const [goLiveDateUTCValue, setGoLiveDateUTCValue] = useState<string | null>(
    null
  );
  useEffect(() => {
    if (
      floor.status === 'complete' ||
      floor.status === 'complete-and-updating'
    ) {
      setGoLiveDateUTCValue(floor.data.go_live_date_utc);
    } else {
      setGoLiveDateUTCValue(null);
    }
  }, [floor]);

  const updateSpace = useCallback(
    (data: FormFields | CoreSpace2024) => {
      setFloor((floor) => {
        if (floor.status !== 'complete') {
          return { status: 'loading' as const };
        }

        return {
          status: 'complete-and-updating' as const,
          data: floor.data,
        };
      });
      CoreAPI.updateSpace(client, plan.floor.id, data as Partial<CoreSpace>)
        .then((response) => {
          setFloor({
            status: 'complete' as const,
            data: response.data as CoreSpace2024,
          });

          toast.success('Updated floor metadata');
        })
        .catch(() => {
          setFloor((floor) => {
            if (floor.status !== 'complete-and-updating') {
              return { status: 'error' as const };
            }
            return {
              status: 'complete' as const,
              data: floor.data,
            };
          });
          toast.error('Error updating floor metadata!');
        });
    },
    [client, plan.floor.id]
  );

  // When the go live date is changed, propagate that to the core api
  const updateFloorGoLiveDateUTC = useCallback(
    (newGoLiveDateUTC: string | null) => {
      setFloor((floor) => {
        if (floor.status !== 'complete') {
          return { status: 'loading' as const };
        }

        return {
          status: 'complete-and-updating' as const,
          data: floor.data,
        };
      });

      CoreAPI.updateSpace(client, plan.floor.id, {
        go_live_date_utc: newGoLiveDateUTC,
      })
        .then((response) => {
          setFloor({
            status: 'complete' as const,
            data: response.data as CoreSpace2024,
          });

          toast.success('Updated plan live date!');
        })
        .catch(() => {
          setFloor((floor) => {
            if (floor.status !== 'complete-and-updating') {
              return { status: 'error' as const };
            }

            return {
              status: 'complete' as const,
              data: floor.data,
            };
          });
          toast.error('Error updating plan live date!');
        });
    },
    [client, plan.floor.id]
  );

  useEffect(() => {
    const justOpened = state.panels.settingsOpen && !wasSettingsOpen.current;
    wasSettingsOpen.current = state.panels.settingsOpen;

    if (!justOpened) {
      return;
    }

    const uniformDebounceValues = getUniformFloorDebounceValues(
      Array.from(state.spaces.items.values())
    );
    if (!uniformDebounceValues) {
      return;
    }

    setFloorDebounceEnabledChoice(uniformDebounceValues.enabledChoice);
    setFloorDebounceTrueMs(uniformDebounceValues.ingressMs);
    setFloorDebounceFalseMs(uniformDebounceValues.egressMs);
  }, [state.panels.settingsOpen, state.spaces]);

  const isDebounceFormDisabled = state.locked || isApplyingFloorDebounce;
  const showMoveToZeroOrigin =
    plan.origin_x_pixels !== 0 ||
    plan.origin_y_pixels !== 0 ||
    plan.origin_angle_degrees !== 0;

  const moveToZeroOrigin = async () => {
    if (isRecenteringOrigin) {
      return;
    }
    setIsRecenteringOrigin(true);
    try {
      await FloorplanAPI.recenterOrigin(client, plan.id);
      toast.success('Moved floorplan to zero-origin.');
      window.location.reload();
    } catch (_error) {
      setIsRecenteringOrigin(false);
      toast.error('Could not move floorplan to zero-origin.');
    }
  };

  return (
    <div className={styles.settingsPanel}>
      <KeybindingsModal
        visible={showKeybindingsModal}
        onCancel={() => setShowKeybindingsModal(false)}
        onDismiss={() => setShowKeybindingsModal(false)}
      />

      {showRecenterConfirm && (
        <Modal
          width={480}
          visible={showRecenterConfirm}
          onBlur={() => {
            if (!isRecenteringOrigin) {
              setShowRecenterConfirm(false);
            }
          }}
        >
          <div style={{ padding: 24 }}>
            <div className={styles.spaceMetaHeader}>Move to zero-origin</div>
            <p style={{ margin: '12px 0 20px', lineHeight: 1.4 }}>
              This change is not reversible. All gathered heatmap metrics will
              be inaccurate.
            </p>
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <Button
                type="hollow"
                size="small"
                disabled={isRecenteringOrigin}
                onClick={() => setShowRecenterConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                type="filled"
                size="small"
                disabled={isRecenteringOrigin}
                onClick={moveToZeroOrigin}
                data-cy="settings-panel-recenter-origin-confirm"
              >
                {isRecenteringOrigin ? 'Moving...' : 'Move to zero-origin'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showSpaceMetadataModal && floor.status === 'complete' && (
        <SettingsPanelForm
          updateSpace={updateSpace}
          visible={showSpaceMetadataModal}
          fieldValues={floor.data as FormFields}
          toggleModal={() => setShowSpaceMetadataModal(!showSpaceMetadataModal)}
        />
      )}
      <Popup
        open={state.panels.settingsOpen}
        onClose={() =>
          dispatch({
            type: 'panels.settings.setState',
            setOpen: false,
          })
        }
        position={{ right: 0, top: 36 }}
        popupWidth={232}
        target={
          <Tooltip
            contents="Plan Settings"
            placement="bottom"
            target={
              <Button
                onClick={() =>
                  dispatch({
                    type: 'panels.settings.setState',
                    setOpen: !state.panels.settingsOpen,
                  })
                }
                trailingIcon={<Icons.CogGearSettings size={18} />}
                size="medium"
                type="outlined"
                data-cy="settings-panel-button"
              />
            }
          />
        }
      >
        <div className={styles.settingsMenuSection}>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelIcon}>
                <Icons.RulerVertical size={16} />
              </div>
              <div className={styles.settingsMenuRowLabelText}>Units</div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              <SelectField
                size="small"
                value={state.displayUnit}
                onChange={(choice) => {
                  dispatch({
                    type: 'settingsPanel.setDisplayUnit',
                    displayUnit: choice.id,
                  });
                }}
                data-cy="settings-panel-unit-select"
                choices={UNIT_CHOICES}
              />
            </div>
          </div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelIcon}>
                <Icons.Paintbrush size={16} />
              </div>
              <div className={styles.settingsMenuRowLabelText}>
                Entity Paste
              </div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              <SelectField
                size="small"
                value={state.entityPasteMode.mode}
                onChange={(choice) => {
                  dispatch({
                    type: 'settingsPanel.setEntityPasteMode',
                    entityPasteMode: {
                      mode: choice.id,
                      affix: 'post',
                      amount: 1,
                    },
                  });
                }}
                data-cy="settings-panel-paste-mode"
                choices={
                  state.copiedEntitySettings && state.copiedEntitySettings.type
                    ? PASTING_CHOICES[state.copiedEntitySettings.type]
                    : PASTING_CHOICES.space
                }
              />
            </div>
          </div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelIcon}>
                <Icons.Keyboard size={16} />
              </div>
              <div className={styles.settingsMenuRowLabelText}>Keybindings</div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              <Button
                size="small"
                onClick={() => {
                  setShowKeybindingsModal(true);
                }}
                type="hollow"
                data-cy="settings-panel-keybindings-button"
              >
                Edit Bindings
              </Button>
            </div>
          </div>
          <div className={styles.settingsMenuRowHeader}>Space</div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelText}>Status</div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              <SelectField
                size="small"
                value={plan.floor.status}
                width={108}
                disabled={state.locked || editStatusLoading}
                leadingIcon={
                  <div
                    style={{
                      width: dust.Space3,
                      height: dust.Space3,
                      borderRadius: '50%',
                      backgroundColor:
                        plan.floor.status === 'planning'
                          ? dust.Yellow400
                          : dust.Green400,
                      marginLeft: dust.Space2,
                    }}
                  />
                }
                onChange={(choice) =>
                  onEditStatus(choice.id).then(() => reloadFloor())
                }
                choices={[
                  { id: 'planning', label: 'Planning' },
                  { id: 'live', label: 'Live' },
                ]}
                data-cy="settings-panel-status"
              />
            </div>
          </div>
          {plan.floor.status === 'live' ? (
            <div className={styles.settingsMenuRow}>
              <div className={styles.settingsMenuRowAction}>
                {floor.status === 'complete' ||
                floor.status === 'complete-and-updating' ? (
                  <DateAndTimeTextField
                    value={goLiveDateUTCValue}
                    onChange={updateFloorGoLiveDateUTC}
                    disabled={
                      state.locked ||
                      editStatusLoading ||
                      floor.status === 'complete-and-updating'
                    }
                    size="small"
                    width="100%"
                    placeholder="eg, 10/05/22 01:00:00 pm"
                    leadingPrefix="Live at:"
                    data-cy="settings-panel-go-live-date"
                  />
                ) : (
                  <TextField
                    error={floor.status === 'error'}
                    placeholder={
                      floor.status === 'error'
                        ? 'Error loading floor!'
                        : 'Loading floor...'
                    }
                    disabled
                    size="small"
                    width="100%"
                  />
                )}
              </div>
            </div>
          ) : null}
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelText}>Floor ID</div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              {isDriftLinksEnabled ? (
                <div className={styles.settingsMenuRowSpaceId}>
                  <Tooltip
                    target={
                      <span
                        className={styles.settingsMenuCopyID}
                        onClick={() => {
                          navigator.clipboard.writeText(plan.floor.id);
                          toast.success('Copied to clipboard.');
                        }}
                      >
                        {plan.floor.id}
                      </span>
                    }
                    contents="Copy Space ID"
                  />
                  <StaffDriftButton spaceId={plan.floor.id} location="floor" />
                </div>
              ) : (
                <div className={styles.settingsMenuRowSpaceId}>
                  {plan.floor.id}
                  <Button
                    size="nano"
                    type="cleared"
                    trailingIcon={<Icons.CopyDuplicate size={14} />}
                    onClick={() => {
                      Analytics.track('Copy Space ID', {
                        spaceType: 'floor',
                        location: 'editor',
                        spaceId: plan.floor.id,
                      });
                      navigator.clipboard.writeText(plan.floor.id);
                      toast.success('Copied to clipboard.');
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelText}>Building ID</div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              {isDriftLinksEnabled ? (
                <div className={styles.settingsMenuRowSpaceId}>
                  <Tooltip
                    target={
                      <span
                        className={styles.settingsMenuCopyID}
                        onClick={() => {
                          navigator.clipboard.writeText(plan.floor.parent_id);
                          toast.success('Copied to clipboard.');
                        }}
                      >
                        {plan.floor.parent_id}
                      </span>
                    }
                    contents="Copy Building ID"
                  />
                  <StaffDriftButton
                    spaceId={plan.floor.parent_id}
                    location="building"
                  />
                </div>
              ) : (
                <div className={styles.settingsMenuRowSpaceId}>
                  {plan.floor.parent_id}
                  <Button
                    size="nano"
                    type="cleared"
                    trailingIcon={<Icons.CopyDuplicate size={14} />}
                    onClick={() => {
                      Analytics.track('Copy Space ID', {
                        spaceType: 'building',
                        location: 'editor',
                        spaceId: plan.floor.id,
                      });
                      navigator.clipboard.writeText(plan.floor.parent_id);
                      toast.success('Copied to clipboard.');
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelText}>
                Floor Metadata
              </div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              <Button
                type="hollow"
                size="small"
                disabled={state.locked}
                onClick={() => setShowSpaceMetadataModal(true)}
              >
                Edit
              </Button>
            </div>
          </div>
          {showMoveToZeroOrigin ? (
            <div className={styles.settingsMenuRow}>
              <div className={styles.settingsMenuRowLabel}>
                <div className={styles.settingsMenuRowLabelText}>
                  Move to zero-origin
                </div>
              </div>
              <div className={styles.settingsMenuRowAction}>
                <Button
                  type="hollow"
                  size="small"
                  disabled={state.locked || isRecenteringOrigin}
                  onClick={() => setShowRecenterConfirm(true)}
                  data-cy="settings-panel-recenter-origin"
                >
                  Apply
                </Button>
              </div>
            </div>
          ) : null}
          <div className={styles.settingsMenuRowHeader}>Tracks</div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowAction}>
              <DateAndTimeTextField
                value={state.tracksStartDate}
                onChange={(date) => {
                  dispatch({
                    type: 'settingsPanel.setTracksStartDate',
                    tracksStartDate: date,
                  });
                }}
                size="small"
                width="100%"
                placeholder="eg, 10/05/22 01:00:00 pm"
                leadingPrefix="Start at:"
              />
            </div>
          </div>
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowAction}>
              <DateAndTimeTextField
                value={state.tracksEndDate}
                onChange={(date) => {
                  dispatch({
                    type: 'settingsPanel.setTracksEndDate',
                    tracksEndDate: date,
                  });
                }}
                size="small"
                width="100%"
                placeholder="eg, 10/05/22 03:00:00 pm"
                leadingPrefix="End at:"
              />
            </div>
          </div>
          {/* number of tracks */}
          <div className={styles.settingsMenuRow}>
            <div className={styles.settingsMenuRowLabel}>
              <div className={styles.settingsMenuRowLabelText}>Tracks</div>
            </div>
            <div className={styles.settingsMenuRowAction}>
              <TextField
                value={state.tracksCount}
                onChange={(e) => {
                  let v: string | number | null = e.currentTarget.value;
                  dispatch({
                    type: 'settingsPanel.setTracksCount',
                    tracksCount: Number(v),
                  });
                }}
                type="number"
                size="small"
                width="60px"
                style={{ textAlign: 'right' }}
              />
            </div>
          </div>

          <div
            className={styles.settingsMenuRowHeader}
            style={{
              cursor: isApplyingFloorDebounce ? 'progress' : 'pointer',
              userSelect: 'none',
              opacity: isApplyingFloorDebounce ? 0.7 : 1,
            }}
            onClick={() => {
              if (isApplyingFloorDebounce) {
                return;
              }
              setIsFloorDebounceExpanded((prev) => !prev);
            }}
            data-cy="settings-panel-floor-debounce-toggle"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div className={styles.settingsMenuRowLabelText}>
                Debounce settings
              </div>
              {isFloorDebounceExpanded ? (
                <Icons.ChevronUp size={16} />
              ) : (
                <Icons.ChevronDown size={16} />
              )}
            </div>
          </div>
          {isFloorDebounceExpanded ? (
            <div
              style={{
                opacity: isApplyingFloorDebounce ? 0.65 : 1,
                pointerEvents: isApplyingFloorDebounce ? 'none' : 'auto',
              }}
            >
              <div className={styles.settingsMenuRow}>
                <div className={styles.settingsMenuRowLabel}>
                  <div className={styles.settingsMenuRowLabelText}>Enabled</div>
                </div>
                <div className={styles.settingsMenuRowAction}>
                  <SelectField
                    size="small"
                    value={floorDebounceEnabledChoice}
                    disabled={isDebounceFormDisabled}
                    onChange={(choice) =>
                      setFloorDebounceEnabledChoice(
                        choice.id as 'null' | 'true' | 'false'
                      )
                    }
                    choices={[
                      { id: 'null', label: 'Not Set' },
                      { id: 'true', label: 'Yes' },
                      { id: 'false', label: 'No' },
                    ]}
                    data-cy="settings-panel-floor-debounce-enabled"
                  />
                </div>
              </div>
              <div className={styles.settingsMenuRow}>
                <div className={styles.settingsMenuRowLabelText}>
                  Ingress (ms)
                </div>
                <TextField
                  size="small"
                  type="number"
                  min="0"
                  step="1"
                  disabled={isDebounceFormDisabled}
                  value={floorDebounceTrueMs}
                  onChange={(e) =>
                    setFloorDebounceTrueMs(e.currentTarget.value)
                  }
                  placeholder="0"
                  width="100%"
                  data-cy="settings-panel-floor-debounce-true-ms"
                />
              </div>
              <div className={styles.settingsMenuRow}>
                <div className={styles.settingsMenuRowLabelText}>
                  Egress (ms)
                </div>
                <TextField
                  size="small"
                  type="number"
                  min="0"
                  step="1"
                  disabled={isDebounceFormDisabled}
                  value={floorDebounceFalseMs}
                  onChange={(e) =>
                    setFloorDebounceFalseMs(e.currentTarget.value)
                  }
                  placeholder="5000"
                  width="100%"
                  data-cy="settings-panel-floor-debounce-false-ms"
                />
              </div>
              <div className={styles.settingsMenuRow}>
                <label
                  className={styles.settingsMenuRowLabel}
                  htmlFor="settings-panel-floor-debounce-include-locked"
                  style={{
                    cursor: isDebounceFormDisabled ? 'default' : 'pointer',
                    fontWeight: '500',
                  }}
                >
                  <div className={styles.settingsMenuRowLabelText}>
                    Include locked spaces
                  </div>
                </label>
                <div className={styles.settingsMenuRowAction}>
                  <input
                    id="settings-panel-floor-debounce-include-locked"
                    type="checkbox"
                    checked={floorDebounceIncludeLocked}
                    disabled={isDebounceFormDisabled}
                    onChange={(e) =>
                      setFloorDebounceIncludeLocked(e.target.checked)
                    }
                    data-cy="settings-panel-floor-debounce-include-locked"
                  />
                </div>
              </div>
              <div className={styles.settingsMenuRow}>
                <div className={styles.settingsMenuRowAction}>
                  <Button
                    type="filled"
                    size="small"
                    width="100%"
                    disabled={isDebounceFormDisabled}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (isApplyingFloorDebounce) {
                        return;
                      }
                      const presenceDebounceEnabled =
                        floorDebounceEnabledChoice === 'true';
                      const trimTrue = floorDebounceTrueMs.trim();
                      const trimFalse = floorDebounceFalseMs.trim();
                      const trueMs =
                        trimTrue === '' ? null : parseInt(trimTrue, 10);
                      const falseMs =
                        trimFalse === '' ? null : parseInt(trimFalse, 10);
                      if (
                        trimTrue !== '' &&
                        (trueMs == null || Number.isNaN(trueMs))
                      ) {
                        toast.error('Invalid ingress delay (ms).');
                        return;
                      }
                      if (
                        trimFalse !== '' &&
                        (falseMs == null || Number.isNaN(falseMs))
                      ) {
                        toast.error('Invalid egress delay (ms).');
                        return;
                      }

                      setIsApplyingFloorDebounce(true);
                      let applyResult: FloorDebounceApplyResult;
                      try {
                        applyResult = await onApplyDebounceToAllSpaces({
                          includeLocked: floorDebounceIncludeLocked,
                          presenceDebounceEnabled,
                          presenceDebounceTrueMs: trueMs,
                          presenceDebounceFalseMs: falseMs,
                        });
                      } catch (_error) {
                        setIsApplyingFloorDebounce(false);
                        toast.error('Error applying debounce settings.');
                        return;
                      }
                      setIsApplyingFloorDebounce(false);

                      if (!applyResult.success) {
                        if (
                          applyResult.errorMessage ===
                          'No unlocked spaces to update.'
                        ) {
                          toast.error(applyResult.errorMessage);
                        }
                        return;
                      }

                      toast.success(
                        `Applied debounce settings to ${applyResult.updatedCount} space(s).`
                      );
                    }}
                    data-cy="settings-panel-floor-debounce-apply"
                  >
                    {isApplyingFloorDebounce
                      ? 'Applying...'
                      : 'Apply to all spaces'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Popup>
    </div>
  );
};

export default SettingsPanel;
